import * as React from "react";

import styles from "../../styles/form-screen";

import { History, Location } from "history";
import { Link, Snackbar, WithStyles, withStyles } from "@material-ui/core";
import BasicLayout, { SnackbarMessage } from "../layouts/basic-layout";

import { KeycloakInstance } from "keycloak-js";
// eslint-disable-next-line max-len
import { Dictionary } from '../../types';
import { Metaform, MetaformFieldType, Reply, MetaformFromJSON, DraftFromJSON, Draft } from "../../generated/client";
import { FieldValue } from "metaform-react";
import Form from "../generic/form";
import Config from "../../config";
import strings from "../../localization/strings";
import Alert from "@material-ui/lab/Alert";
import EmailDialog from "../generic/email-dialog";
import Mail from "../../mail/mail";
import ConfirmDialog from "../generic/confirm-dialog";
import { FileFieldValue, ValidationErrors } from "metaform-react/dist/types";

const AUTOSAVE_COOLDOWN = 500;

/**
 * Component props
 */
interface Props extends WithStyles<typeof styles> {
  history: History;
  location: Location;
}

/**
 * The form for rendering a metaform
 */
const form = require(`../../files/${process.env.REACT_APP_FORM_NAME}`);

/**
 * Component state
 */
interface State {
  snackbarMessage?: SnackbarMessage;
  error?: string | Error | Response;
  loading: boolean;
  saving: boolean;
  autosaving: boolean;
  formValid: boolean;
  draftSavedVisible: boolean;
  draftSaveVisible: boolean;
  draftEmailDialogVisible: boolean;
  draftId: string | null;
  replySavedVisible: boolean;
  replyEmailDialogVisible: boolean;
  replyDeleteVisible: boolean;
  replyDeleteConfirmVisible: boolean;
  reply?: Reply;
  ownerKey: string | null;
  metaform?: Metaform;
  formValues: Dictionary<FieldValue>;
  redirectTo?: string;
  draft?: Draft;
}

/**
 * Component for exhibitions screen
 */
export class FormScreen extends React.Component<Props, State> {

  private formValueChangeTimeout: any = null;

  /**
   * Constructor
   *
   * @param props component properties
   */
  constructor(props: Props) {
    super(props);
    this.state = {      
      loading: false,
      saving: false,
      autosaving: false,
      formValid: true,
      draftSaveVisible: false,
      draftSavedVisible: false,
      draftEmailDialogVisible: false,
      replySavedVisible: false,
      replyEmailDialogVisible: false,
      replyDeleteVisible: false,
      replyDeleteConfirmVisible: false,
      draftId: null,
      ownerKey: null,
      formValues: {}
    };
  }

  /**
   * Component did mount life cycle event
   */
  public componentDidMount = async () => {
    const { location } = this.props;

    const query = new URLSearchParams(location.search);

    const draftId = query.get("draft");
    const replyId = query.get("reply");
    const ownerKey = query.get("owner-key");

    const draft = DraftFromJSON(form);

    try {
      this.setState({
        loading: true,
        draftId: draftId,
        draft: draft
      });


      const metaform = MetaformFromJSON(form);
      console.log("Metaform is ", metaform);
      
      const formValues = { ...this.state.formValues };
      document.title = metaform.title ? metaform.title : "Metaform";

      metaform.sections?.forEach(section => {        
        section.fields?.forEach(field => {
          const { name, _default, options } = field;
          if (field.type === MetaformFieldType.Files && !field.uploadUrl) {
            field.uploadUrl = ""
          } 
          if (name) {
            if (_default) {
              formValues[name] = _default;
            } else if (options && options.length) {
              const selectedOption = options.find(option => option.selected || option.checked);
              if (selectedOption) {
                formValues[name] = selectedOption.name;
              }
            }
          }
        });
      });

      if (replyId && ownerKey) {
        const reply = await this.findReply(replyId, ownerKey);
        if (reply) {
          const replyData = await this.processReplyData(metaform, reply, ownerKey);
          if (replyData) {
            Object.keys(replyData as any).forEach(replyKey => {
              formValues[replyKey] = replyData[replyKey] as any;
            });
          }

          this.setState({
            reply: reply,
            ownerKey: ownerKey,
            replyDeleteVisible: !!ownerKey
          });
        } else {
          this.setState({
            snackbarMessage: {
              message: strings.formScreen.replyNotFound,
              severity: "error"
            }
          });
        }        
      } else if (draftId) {
        const draft = await this.findDraft(draftId);
        const draftData = draft?.data || {};
        Object.keys(draftData).forEach(draftKey => {
          formValues[draftKey] = draftData[draftKey] as any;
        });
      }

      this.setState({
        metaform: metaform,
        formValues: formValues,
        loading: false
      });
    } catch (e) {
      if (e.status === 403) {
        this.setState({
          redirectTo: "/protected/form"
        });
      } else {
        this.setState({
          loading: false,
          error: e
        });
      }
    }
  }

  /**
   * Component render method
   */
  public render = () => {
    const { classes } = this.props;
    const { redirectTo, loading, saving, snackbarMessage, error } = this.state;

    return (
      <BasicLayout 
        loading={ loading || saving } 
        loadMessage={ saving ? strings.formScreen.savingReply : undefined } 
        snackbarMessage={ snackbarMessage } 
        error={ error } 
        redirectTo={ redirectTo }
        clearError={ this.clearError } 
        clearSnackbar={ this.clearSnackbar }
      >
        <div className={ classes.root }>
          { this.renderForm() }
          { this.renderReplySaved() }
          { this.renderReplyEmailDialog() }
          { this.renderReplyDelete() }
          { this.renderReplyDeleteConfirm() }
          { this.renderDraftSave() }
          { this.renderDraftSaved() }
          { this.renderDraftEmailDialog() }
          { this.renderAutosaving() }
        </div>
      </BasicLayout>
    );
  }

  /**
   * Renders the form
   */
  private renderForm = () => {
    const { metaform } = this.state;

    if (!metaform) {
      return null
    }

    return (
      <Form
        ownerKey={ this.state.ownerKey || "" }
        contexts={ ["FORM"] }    
        metaform={ metaform }
        getFieldValue={ this.getFieldValue }
        setFieldValue={ this.setFieldValue }
        onSubmit={ this.onSubmit }
        onValidationErrorsChange={ this.onValidationErrorsChange }
      />
    );
  }

    /**
   * Processes reply from server into form that is understood by ui
   * 
   * @param metaform metaform that is being viewed
   * @param reply reply loaded from server
   * @param ownerKey owner key for the reply
   * 
   * @return data processes to be used by ui
   */
  private processReplyData = async (metaform: Metaform, reply: Reply, ownerKey: string) => {
    
    let values = reply.data;
    for (let i = 0; i < (metaform.sections || []).length; i++) {
      let section = metaform.sections && metaform.sections[i] ? metaform.sections[i] : undefined;
      if (section) {
        for (let j = 0; j < (section.fields || []).length; j++) {
          let field = section.fields && section.fields[j] ? section.fields[j] : undefined;
          if (field &&
              field.type === MetaformFieldType.Files &&
              values &&
              field.name &&
              values[field.name]) {
            let fileIds = Array.isArray(values[field.name]) ? values[field.name] : [values[field.name]]; 
            let attachmentPromises = (fileIds as string[]).map((fileId) => {
              return null;
            });
            let attachments = await Promise.all(attachmentPromises);
            values[field.name] = {
              files: attachments.map((a) => {
                return null;
              })
            };
          }
        }
      }
    }
    return values;
  }

  /**
   * Renders draft save dialog
   */
  private renderDraftSave = () => {
    const { draftSaveVisible } = this.state;

    return (
      <Snackbar open={ draftSaveVisible }  onClose={ this.onDraftSaveClose }>
        <Alert onClose={ this.onDraftSaveClose } severity="info">
          <span> { strings.formScreen.saveDraft } </span>
          <Link href="#" onClick={ this.onSaveDraftLinkClick }> { strings.formScreen.saveDraftLink } </Link>
        </Alert>
      </Snackbar>
    );
  }

  /**
   * Renders draft saved dialog
   */
  private renderDraftSaved = () => {
    const { draftSavedVisible } = this.state;
    const draftLink = this.getDraftLink();

    if (!draftLink) {
      return null;
    }

    return (
      <Snackbar open={ draftSavedVisible } onClose={ this.onDraftSavedClose } anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={ this.onDraftSavedClose } severity="success">
          <span> { strings.formScreen.draftSaved } </span>
          <br/><br/>
          <a href={ draftLink }> { draftLink } </a>
          <p> 
            { strings.formScreen.draftEmailText } 
            <Link href="#" onClick={ this.onDraftEmailLinkClick }> { strings.formScreen.draftEmailLink } </Link>
          </p>
        </Alert>
      </Snackbar>
    );
  }

  /**
   * Renders autosave dialog
   */
  private renderAutosaving = () => {
    const { autosaving } = this.state;

    return (
      <Snackbar open={ autosaving }>
        <Alert severity="info">
          <span> { strings.formScreen.autosaving } </span>
        </Alert>
      </Snackbar>
    );
  }

  /**
   * Renders reply email dialog
   */
  private renderDraftEmailDialog = () => {
    return (
      <EmailDialog 
        text={ strings.formScreen.draftEmailDialogText }
        open={ this.state.draftEmailDialogVisible }
        onSend={ this.onDraftEmailDialogSend }
        onCancel={ this.onDraftEmailDialogCancel }
      />
    );
  }

  /**
   * Renders reply saved dialog
   */
  private renderReplySaved = () => {
    const replyEditLink = this.getReplyEditLink();

    if (replyEditLink) {      
      return (
        <Snackbar open={ this.state.replySavedVisible } onClose={ this.onReplySavedClose } anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert onClose={ this.onReplySavedClose  } severity="success">
            <span> { strings.formScreen.replySaved } </span>
            <p> { strings.formScreen.replyEdit } </p>
            <div style={{ textOverflow: "ellipsis", overflow: "hidden", maxWidth: "50vw", whiteSpace: "nowrap" }}>
              <a href={ replyEditLink }> { replyEditLink } </a>
            </div>
            <p> 
              { strings.formScreen.replyEditEmailText } 
              <Link href="#" onClick={ this.onReplyEmailLinkClick }> { strings.formScreen.replyEditEmailLink } </Link>
            </p>
          </Alert>
        </Snackbar>
      );
    } else {
      return (
        <Snackbar open={ this.state.replySavedVisible } onClose={ this.onReplySavedClose } anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert onClose={ this.onReplySavedClose  } severity="success">
            <span> { strings.formScreen.replySaved } </span>
          </Alert>
        </Snackbar>
      );
    }
  }

  /**
   * Renders reply email dialog
   */
  private renderReplyEmailDialog = () => {
    return (
      <EmailDialog 
        text={ strings.formScreen.replyEditEmailDialogText }
        open={ this.state.replyEmailDialogVisible }
        onSend={ this.onReplyEmailDialogSend }
        onCancel={ this.onReplyEmailDialogCancel }
      />
    );
  }

  /**
   * Renders reply delete dialog
   */
  private renderReplyDelete = () => {
    return (
      <Snackbar open={ this.state.replyDeleteVisible }  onClose={ this.onReplyDeleteClose }>
        <Alert onClose={ this.onReplyDeleteClose  } severity="warning">
          <span> { strings.formScreen.replyDeleteText } </span>
          <Link href="#" onClick={ this.onReplyDeleteLinkClick }> { strings.formScreen.replyDeleteLink } </Link>
        </Alert>
      </Snackbar>
    );
  }

  /**
   * Renders delete reply confirm dialog
   */
  private renderReplyDeleteConfirm = () => {
    return (
      <ConfirmDialog
        onClose={ this.onReplyDeleteConfirmClose }
        onCancel={ this.onReplyDeleteConfirmClose }
        onConfirm={ this.onReplyDeleteConfirmConfirm }
        cancelButtonText={ strings.generic.cancel }
        positiveButtonText={ strings.generic.confirm }
        title={ strings.formScreen.confirmDeleteReplyTitle }
        text={ strings.formScreen.confirmDeleteReplyText }
        open={ this.state.replyDeleteConfirmVisible } 
      />
    );
  }

  /**
   * Method for getting field value
   *
   * @param fieldName field name
   */
  private getFieldValue = (fieldName: string): FieldValue => {
    console.log("Field value ", this.state.formValues[fieldName], fieldName);
    return this.state.formValues[fieldName];
  }

  /**
   * Method for setting field value
   *
   * @param fieldName field name
   * @param fieldValue field value
   */
  private setFieldValue = (fieldName: string, fieldValue: FieldValue) => {
    const { formValues, metaform, formValid } = this.state;

    if (formValues[fieldName] !== fieldValue) {
      formValues[fieldName] = fieldValue;
      
      this.setState({
        formValues: formValues,
        draftSaveVisible: !!this.state.metaform?.allowDrafts
      });

      if (formValid && metaform?.autosave) {
        this.scheduleAutosave();
      }
    }
  }

  /**
   * Clears error
   */
  private clearError = () => {
    this.setState({ 
      error: undefined 
    });
  }

  /**
   * Clears snackbar message
   */
  private clearSnackbar = () => {
    this.setState({ 
      snackbarMessage: undefined 
    });
  }

  /**
   * Finds the draft from API
   * 
   * @param draftId draft id
   * @returns found draft or null if not found
   */
  private findDraft = async (draftId: string) => {
    try {
      const draft = DraftFromJSON(form);
      return draft;
    } catch (e) {
      return null;
    }
  }

  /**
   * Finds the reply from API
   * 
   * @param replyId reply id
   * @param ownerKey owner key
   * @returns found reply or null if not found
   */
  private findReply = async (replyId: string, ownerKey: string) => {
    try {
      const metaformId = Config.getMetaformId();   

      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Save the reply as draft
   */
  private saveDraft = async () => {
    try {
      const { metaform, formValues, draftId, draft } = this.state;
      
      if (!draft) {
        return;
      }

      const draftToSave: Draft = {
        ...draft,
        data: formValues as any
      }

      console.log("Draft to SAVE ", draftToSave);

      this.setState({
        draft: draftToSave
      });

    } catch (e) {
      this.setState({
        loading: false,
        error: e
      });
    }
  }

  /**
   * Sends draft link to given email
   * 
   * @param email email
   */
  private sendDraftEmail = async (email: string) => {
    const { metaform } = this.state;
    const { REACT_APP_EMAIL_FROM } = process.env;
    const draftLink = this.getDraftLink();
    
    if (!draftLink || !metaform) {
      return;
    }

    try {
      this.setState({
        draftEmailDialogVisible: false,
        loading: true
      });

      if (!REACT_APP_EMAIL_FROM) {
        throw new Error("Missing REACT_APP_EMAIL_FROM env");
      }

      const formTitle = metaform.title || "";
      const subject = strings.formatString(strings.formScreen.draftEmailSubject, formTitle) as string;
      const html = strings.formatString(strings.formScreen.draftEmailContent, formTitle, draftLink) as string;

      await Mail.sendMail({
        from: REACT_APP_EMAIL_FROM,
        html: html,
        subject: subject,
        to: email
      });

      this.setState({
        loading: false,
        snackbarMessage: {
          message: strings.formScreen.draftEmailSent,
          severity: "success"
        }
      });
    } catch (e) {
      this.setState({
        loading: false,
        error: e
      });
    }
  }

  /**
   * Sends reply link to given email
   * 
   * @param email email
   */
  private sendReplyEmail = async (email: string) => {
    const { metaform } = this.state;
    const { REACT_APP_EMAIL_FROM } = process.env;
    const replyEditLink = this.getReplyEditLink();
    
    if (!replyEditLink || !metaform) {
      return;
    }

    try {
      this.setState({
        replyEmailDialogVisible: false,
        loading: true
      });

      if (!REACT_APP_EMAIL_FROM) {
        throw new Error("Missing REACT_APP_EMAIL_FROM env");
      }

      const formTitle = metaform.title || "";
      const subject = strings.formatString(strings.formScreen.replyEditEmailSubject, formTitle) as string;
      const html = strings.formatString(strings.formScreen.replyEditEmailContent, formTitle, replyEditLink) as string;

      await Mail.sendMail({
        from: REACT_APP_EMAIL_FROM,
        html: html,
        subject: subject,
        to: email
      });

      this.setState({
        loading: false,
        snackbarMessage: {
          message: strings.formScreen.replyEditEmailSent,
          severity: "success"
        }
      });
    } catch (e) {
      this.setState({
        loading: false,
        error: e
      });
    }
  }

  /**
   * Deletes the reply
   */
  private deleteReply = async () => {
    const { reply, ownerKey } = this.state;

    try {
      this.setState({
        replyDeleteConfirmVisible: false,
        loading: true
      });

      if (reply && reply.id && ownerKey) {
        console.log("Deleting reply");
      } else {
        throw new Error("Missing parameters, failed to delete reply");
      }

      this.setState({
        loading: false,
        replyDeleteVisible: false,
        reply: undefined,
        ownerKey: null,
        snackbarMessage: {
          message: strings.formScreen.replyDeleted,
          severity: "success"
        }
      });
    } catch (e) {
      this.setState({
        loading: false,
        error: e
      });
    }
  }

  /**
   * Returns draft link
   * 
   * @returns draft link or null if not available
   */
  private getDraftLink = () => {
    const { draftId } = this.state;
    if (!draftId) {
      return null;
    }

    const { location } = window;
    return (new URL(`${location.protocol}//${location.hostname}:${location.port}${location.pathname}?draft=${draftId}`)).toString();
  }

  /**
   * Returns reply edit link
   * 
   * @returns reply edit link or null if not available
   */
  private getReplyEditLink = () => {
    const { reply, ownerKey } = this.state;
    if (!reply?.id || !ownerKey) {
      return null;
    }

    return null;
  }

  /**
   * Returns form values as map
   * 
   * @param metaform metaform
   * @returns form values as map
   */
  private getFormValues = (metaform: Metaform): { [ key: string]: object } => {
    const { formValues } = this.state;
    const values = { ...formValues };

    metaform.sections?.forEach((section) => {
      section.fields?.forEach((field) => {
        if (field.type === MetaformFieldType.Files) {
          let value = this.getFieldValue(field.name as string);
          if ( !value ) {
            value = { files: [] }
          }
          values[field.name as string] = (value as FileFieldValue).files.map(file => file.id);
        } 
      })
    });

    return values as { [ key: string]: object };
  }

  /**
   * Saves the reply
   */
  private saveReply = async () => {
    const { metaform, ownerKey } = this.state;
    let { reply } = this.state;

    if (!metaform || !metaform.id) {
      return;
    }

    this.setState({
      saving: true
    });

    try {
      if (reply && reply.id && ownerKey) {
        reply = await this.updateReply(metaform, reply, ownerKey);        
      } else {
        reply = await this.createReply(metaform);
      }

      const updatedOwnerKey = ownerKey || reply?.ownerKey;
      let updatedValues = reply?.data;
      if (updatedOwnerKey && reply) {
        updatedValues = await this.processReplyData(metaform, reply, updatedOwnerKey);
      }
      this.setState({
        saving: false,
        replySavedVisible: true,
        reply: reply,
        ownerKey: updatedOwnerKey || null,
        formValues: updatedValues as any 
      });
    } catch (e) {
      this.setState({
        saving: false,
        error: e
      });
    };
  }

  /**
   * Creates new reply
   * 
   * @param metaform metaform
   * @returns created reply
   */
  private createReply = async (metaform: Metaform) => {

    const mockupReply: Reply = {
      id: metaform.id,
      createdAt: new Date(),
      modifiedAt: new Date(),
      userId: "",
      data: this.getFormValues(metaform),
    };

    return mockupReply;
  }

  /**
   * Updates existing reply
   * 
   * @param metaform metaform
   * @param reply reply
   * @param ownerKey owner key
   * @returns updated reply
   */
  private updateReply = async (metaform: Metaform, reply: Reply, ownerKey: string | null) => {

    const mockupReply: Reply = {
      id: metaform.id,
      createdAt: new Date(),
      modifiedAt: new Date(),
      userId: "",
      data: this.getFormValues(metaform),
    };

    return mockupReply;
  }

  /**
   * Schedules an autosave. If new autosave is scheduled before given cooldown period 
   * the old autosave is cancelled and replaced with the new one
   */
  private scheduleAutosave = () => {
    if (this.formValueChangeTimeout) {
      clearTimeout(this.formValueChangeTimeout);
      this.formValueChangeTimeout = null;
    }

    this.formValueChangeTimeout = setTimeout(this.autosave, AUTOSAVE_COOLDOWN);
  }

  /**
   * Autosaves the form
   */
  private autosave = async () => {
    this.formValueChangeTimeout = null;

    const { formValid, metaform, ownerKey, autosaving } = this.state;
    let { reply } = this.state;

    if (!formValid) {
      return;
    }

    if (autosaving) {
      this.scheduleAutosave();
      return;
    }

    if (!metaform || !metaform.id || !reply) {
      return;
    }

    try {
      this.setState({
        autosaving: true
      });

      await this.updateReply(metaform, reply, ownerKey);        

      this.setState({
        autosaving: false
      });
    } catch (e) {
      this.setState({
        autosaving: false,
        error: e
      });
    };
  }

  /**
   * Method for submitting form
   */
  private onSubmit = async () =>  {
    await this.saveReply();
  }

  /**
   * Event handler for validation errors change
   * 
   * @param validationErrors validation errors
   */
  private onValidationErrorsChange = (validationErrors: ValidationErrors) => {
    const { metaform } = this.state;

    const formValid = Object.keys(validationErrors).length === 0;

    if (formValid !== this.state.formValid) {
      this.setState({
        formValid: formValid
      });

      if (formValid && metaform?.autosave) {
        this.scheduleAutosave();
      }
    }
  }

  /**
   * Event handler for draft save snackbar close
   */
  private onDraftSaveClose = () => {
    this.setState({
      draftSaveVisible: false
    });
  }

  /**
   * Event handler for draft save snackbar close
   */
  private onDraftSavedClose = () => {
    this.setState({
      draftSavedVisible: false
    });
  }

  /**
   * Event handler for draft save link click
   */
  private onSaveDraftLinkClick = () => {
    this.saveDraft();
  }

  /**
   * Event handler for draft email link click
   */
  private onDraftEmailLinkClick = () => {
    this.setState({
      draftSavedVisible: false,
      draftEmailDialogVisible: true
    });
  }

  /**
   * Event handler for draft email dialog send click
   * 
   * @param email email
   */
  private onDraftEmailDialogSend = (email: string) => {
    this.sendDraftEmail(email);
  }

  /**
   * Event handler for draft email dialog cancel click
   * 
   * @param email email
   */
  private onDraftEmailDialogCancel = () => {
    this.setState({
      draftEmailDialogVisible: false
    });
  }

  /**
   * Event handler for reply email link click
   */
  private onReplyEmailLinkClick = () => {
    this.setState({
      replySavedVisible: false,
      replyEmailDialogVisible: true
    });
  }

  /**
   * Event handler for reply email dialog send click
   * 
   * @param email email
   */
  private onReplyEmailDialogSend = (email: string) => {
    this.sendReplyEmail(email);
  }

  /**
   * Event handler for reply email dialog cancel click
   * 
   * @param email email
   */
  private onReplyEmailDialogCancel = () => {
    this.setState({
      replyEmailDialogVisible: false
    });
  }

  /**
   * Event handler for reply saved snackbar close
   */
  private onReplySavedClose  = () => {
    this.setState({
      replySavedVisible: false
    });
  }

  /**
   * Event handler for reply delete snackbar close
   */
  private onReplyDeleteClose = () => {
    this.setState({
      replyDeleteVisible: false
    });
  }

  /**
   * Event handler for reply delete link click
   */
  private onReplyDeleteLinkClick = () => {
    this.setState({
      replyDeleteConfirmVisible: true
    });
  }

  /**
   * Event handler for reply delete confirm dialog close
   */
  private onReplyDeleteConfirmClose = () => {
    this.setState({
      replyDeleteConfirmVisible: false
    });
  }
  
  /**
   * Event handler for reply delete confirm dialog confirm
   */
  private onReplyDeleteConfirmConfirm = () => {
    this.deleteReply();
  }

}


export default withStyles(styles)(FormScreen);
